import React from "react";
//#region types
import { FormFieldProps, TextAreaExtraProps } from "models/propTypes/genericFormProps";
//#endregion
//#region components
import usePlaceholder from "../hooks/usePlaceholder";
import ReadOnlyField from "../baseFields/readOnlyField";
import InputField from "../baseFields/inputField";
import { FormControl } from "@mui/material";
import { useFormikContext } from "formik";
//#endregion
import { FormHelper, LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

function TextAreaFormField<T extends Record<string, any>>(props: FormFieldProps<T>) {
  const { name, field, errors, value, success, handleOnChange, handleOnBlurCustom, optional } = props;

  const { values } = useFormikContext<T>();
  const hasError = !!errors;

  const textAreaExtraProps = field.extraProps as TextAreaExtraProps;

  const [readOnlyValue, setReadOnlyValue] = React.useState<string>();
  React.useEffect(() => {
    if (props.readonly) {
      const currentValue = FormHelper.getFormattedValue<T>(value, values, field);
      if (currentValue !== readOnlyValue) setReadOnlyValue(currentValue);
    }
  }, [values, value]);

  const placeholder = usePlaceholder(field.label, field.placeholder, optional);

  return props.readonly ? (
    <ReadOnlyField label={field.label} className={cssExports.readOnlyTextField} href={field.href}>
      {readOnlyValue}
    </ReadOnlyField>
  ) : (
    <FormControl fullWidth className={cssExports.inputFormControl}>
      <InputField
        labelPlacement={field.rightAlign ? "right" : "left"}
        label={LocalizationHelperInstance.getFormattedMessage(field.label, field.label)}
        placeholder={placeholder}
        inputBaseProps={{
          value: value,
          name: name,
          type: "text",
          error: hasError,
          onBlur: handleOnBlurCustom.bind(null, name),
          onFocus: field.onFocusHandler,
          onChange: handleOnChange.bind(null, name),
          multiline: true,
          rows: textAreaExtraProps?.minRows === undefined ? (textAreaExtraProps?.rows ?? 5) : undefined,
          minRows: textAreaExtraProps?.minRows,
          maxRows: textAreaExtraProps?.maxRows,
          disabled: props.disabled,
          readOnly: props.readonly,
        }}
        icon={field.icon}
        disabled={props.disabled}
        success={success}
        maxLength={field.maxLength}
        className={field.classes?.input}
        isLoading={props.loading}
      />
    </FormControl>
  );
}

export default TextAreaFormField;
